import DataTableUsersHistory from "@/components/data-table-users-history/data-table-users-history";
import { DatePickerWithRange } from "@/components/date-range-picker";
import { TeamActionsChart } from "@/components/team/team-actions-chart";
import { TeamBlockError } from "@/components/team/team-block-error";
import { TeamTimeSavedChart } from "@/components/team/team-time-saved-chart";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useTeams } from "@/contexts/teams-provider";
import { useApi } from "@/hooks/useApi";
import { useApiQuery } from "@/hooks/useApiQuery";
import { useTranslation } from "@/hooks/useTranslation";
import { getUserColor } from "@/utils/user-color";
import { ActionDTO, TimeSavedDTO, UserHistoryDTO } from "@shaarp/common";
import { Activity, HelpCircle, MenuIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { DateRange } from "react-day-picker";

export default function TeamActivityPage() {
  const api = useApi();
  const { t } = useTranslation();
  const { toast } = useToast();
  const toastError = useToastError();
  const { teamSelected } = useTeams();

  // The last seven whole days. Every later range comes from
  // DatePickerWithRange, which already snaps its bounds to 00:00 and 23:59:59,
  // so this is the only place the widening has to happen.
  const [date, setDate] = useState<DateRange>(() => {
    const from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    from.setHours(0, 0, 0, 0);
    const to = new Date();
    to.setHours(23, 59, 59, 999);

    return { from, to };
  });
  const [weeklyReportEnabled, setWeeklyReportEnabled] = useState<boolean>(
    teamSelected?.featureReportingWeeklyActivityEnabled ?? false,
  );
  const [isTogglingReport, setIsTogglingReport] = useState<boolean>(false);

  const handleToggleWeeklyReport = async (newValue: boolean) => {
    try {
      setIsTogglingReport(true);
      setWeeklyReportEnabled(newValue);
      const { data } = await api.post<{ enabled: boolean }>(
        `/my/team/activity-report`,
        { enabled: newValue },
      );
      setWeeklyReportEnabled(data.enabled);
      toast({
        variant: "default",
        title: t("team.activity.weeklyReport.toast.success.title"),
        description: newValue
          ? t("team.activity.weeklyReport.toast.success.descriptionEnabled")
          : t("team.activity.weeklyReport.toast.success.descriptionDisabled"),
      });
    } catch (_) {
      setWeeklyReportEnabled(!newValue);
      toastError();
    } finally {
      setIsTogglingReport(false);
    }
  };

  // Serialise the range once, so the three query keys below stay stable across
  // renders.
  const { from, to } = useMemo(
    () => ({
      from: date.from?.toISOString(),
      to: date.to?.toISOString(),
    }),
    [date],
  );

  const teamId = teamSelected?.id;
  // The team id belongs to every key: switching teams must not serve another
  // team's cached activity.
  const isRangeComplete = Boolean(from && to);

  // One query per block: a failing endpoint only blanks its own card, and each
  // block gets its own retry.
  const historyQuery = useApiQuery<UserHistoryDTO[]>({
    key: ["team-activity", "history", teamId, from, to],
    path: "/my/team/users/history",
    params: { from, to },
    enabled: isRangeComplete,
  });

  const timeSavedQuery = useApiQuery<TimeSavedDTO[]>({
    key: ["team-activity", "time-saved", teamId, from, to],
    path: "/my/team/users/time-saved",
    params: { from, to },
    enabled: isRangeComplete,
  });

  const actionsQuery = useApiQuery<ActionDTO[]>({
    key: ["team-activity", "actions", teamId, from, to],
    path: "/my/team/users/actions",
    params: { from, to },
    enabled: isRangeComplete,
  });

  const usersHistory = useMemo(
    () => historyQuery.data ?? [],
    [historyQuery.data],
  );

  const timeSavedData = useMemo(
    () => (timeSavedQuery.data ?? []).filter((item) => item.timeSaved > 0),
    [timeSavedQuery.data],
  );

  const actionsData = useMemo(
    () => actionsQuery.data ?? [],
    [actionsQuery.data],
  );

  // Collect all unique user IDs from both datasets
  const allUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of timeSavedData) {
      ids.add(item.userId);
    }
    for (const action of actionsData) {
      for (const user of action.users) {
        ids.add(user.userId);
      }
    }
    return Array.from(ids);
  }, [timeSavedData, actionsData]);

  // Pre-compute colors for all users
  const userColors = useMemo(() => {
    const colors = new Map<string, string>();
    for (const userId of allUserIds) {
      colors.set(userId, getUserColor(userId, allUserIds));
    }
    return colors;
  }, [allUserIds]);

  // The global empty state only stands for "this team did nothing": it requires
  // the three calls to have actually succeeded, so a failure is never mistaken
  // for an idle period.
  const isEmpty =
    historyQuery.isSuccess &&
    timeSavedQuery.isSuccess &&
    actionsQuery.isSuccess &&
    usersHistory.length === 0 &&
    timeSavedData.length === 0 &&
    actionsData.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("team.activity.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("team.activity.description")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t("team.activity.period")}
          </span>
          <DatePickerWithRange
            defaultDate={date}
            onChange={(value) => setDate(value as DateRange)}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MenuIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <div className="flex items-center justify-between p-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    {t("team.activity.weeklyReport.label")}
                  </span>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <p>{t("team.activity.weeklyReport.tooltip")}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Switch
                  checked={weeklyReportEnabled}
                  onCheckedChange={handleToggleWeeklyReport}
                  disabled={isTogglingReport}
                />
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {isEmpty ? (
        <Empty className="border py-16">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Activity />
            </EmptyMedia>
            <EmptyTitle>{t("team.activity.empty.title")}</EmptyTitle>
            <EmptyDescription>
              {t("team.activity.empty.description")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>
                  {t("team.timeSaved.title")}
                </CardTitle>
                <CardDescription>
                  {t("team.timeSaved.description")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {timeSavedQuery.isError ? (
                  <TeamBlockError
                    onRetry={() => void timeSavedQuery.refetch()}
                    boxed
                  />
                ) : (
                  <TeamTimeSavedChart
                    data={timeSavedData}
                    userColors={userColors}
                    isLoading={timeSavedQuery.isLoading}
                  />
                )}
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>{t("team.actions.title")}</CardTitle>
                <CardDescription>
                  {t("team.actions.description")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {actionsQuery.isError ? (
                  <TeamBlockError
                    onRetry={() => void actionsQuery.refetch()}
                    boxed
                  />
                ) : (
                  <TeamActionsChart
                    data={actionsData}
                    userColors={userColors}
                    isLoading={actionsQuery.isLoading}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {historyQuery.isError && (
            <Card>
              <CardContent className="p-6">
                <TeamBlockError onRetry={() => void historyQuery.refetch()} />
              </CardContent>
            </Card>
          )}

          {!historyQuery.isError &&
            !historyQuery.isLoading &&
            usersHistory.length > 0 && (
              <Card>
                <CardContent className="p-6 space-y-4">
                  <DataTableUsersHistory usersHistory={usersHistory} />
                </CardContent>
              </Card>
            )}
        </>
      )}
    </div>
  );
}
